/**
 * CompanyCard 컴포넌트 - 회사 카드
 */

import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, MapPin, Phone, Pencil, Trash2 } from 'lucide-react';
import type { Company, CompanyTranslations } from './types';

interface CompanyCardProps {
  company: Company;
  onEdit: (company: Company) => void;
  onDelete: (company: Company) => void;
  onSelect: (company: Company) => void;
  translations: CompanyTranslations;
}

export function CompanyCard({
  company,
  onEdit,
  onDelete,
  onSelect,
  translations: t
}: CompanyCardProps) {
  return (
    <Card className="p-3 bg-background rounded-[5px] border border-border">
      <div className="flex flex-col gap-2">
        {/* 회사 정보 */}
        <div>
          <h3 className="text-[15px] font-semibold text-foreground mb-1.5 break-words">
            {company.name}
          </h3>
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="break-all">{company.rfc}</span>
            </div>
            <div className="flex items-start gap-2 text-[12px] text-muted-foreground">
              <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
              <span className="line-clamp-1 break-words">{company.address}</span>
            </div>
            {company.phone && (
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <Phone className="w-3 h-3 shrink-0" />
                <span className="break-all">{company.phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="flex gap-1.5 pt-1">
          {/* 수정 버튼 */}
          <button
            onClick={() => onEdit(company)}
            className="flex-1 flex items-center justify-center gap-1 bg-muted hover:bg-muted/80 text-foreground h-9 rounded-[5px] text-[12px] font-medium"
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>{t.edit}</span>
          </button>

          {/* 삭제 버튼 */}
          <button
            onClick={() => onDelete(company)}
            className="flex-1 flex items-center justify-center gap-1 bg-destructive/10 hover:bg-destructive/20 text-destructive h-9 rounded-[5px] text-[12px] font-medium border-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>{t.delete}</span>
          </button>

          {/* 선택 버튼 */}
          <Button
            onClick={() => onSelect(company)}
            className="flex-1 bg-primary hover:bg-primary-hover text-white h-9 rounded-[5px] text-[12px] font-medium"
          >
            {t.selectCompany}
          </Button>
        </div>
      </div>
    </Card>
  );
}
